import styles from "./Step2.module.css";
import SubmitButton from "../../../../../components/Button/SubmitButton/SubmitButton";
import AddressInput from "../../Inputs/Address/Address";
import DateInput from "../../Inputs/Date/Date";
import CurrencyInput from "../../Inputs/Currency/Currency";
import { useOrderContext } from "../../../../../context/OrderContext";
import { useTranslation } from "react-i18next";
import { StepProps } from "../../../../../types/checkout.types";
import { Subscriber } from "../../../../../types/auth.types";
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import SecondaryButton from "../../../../../components/Button/SecondaryButton/SecondaryButton";
import { ChevronLeft } from "lucide-react";
import { useUserContext } from "../../../../../context/UserContext";
import { useCartContext } from "../../../../../context/CartContext";
import { useStorageItems } from "../../../../../context/StorageItemsContext";
import { useQuickLinks } from "../../../../../hooks/useQuickLinks";
import { useProducts } from "../../../../../context/ProductContext";
import { normalizeDate, filterDatesOnOrAfterEffectiveTomorrow } from "../../../../../utils/calendar";
import { hasTransportForItems } from "../../../../../utils/cart";

/**
 * A Checkout step 2 component.
 * Contains the complete order form: event name, delivery room, address, delivery date,
 * recipient/phone details, billing/payment method, note and internal code.
 *
 * @param props StepProps
 * @param {() => void} props.onContinue - Increment step handler
 * @returns TSX.Element
 */
const Step2 = ({ onContinue, onBack, actionsContainer }: StepProps) => {
    const { t } = useTranslation(); // Translation
    const { orderData, handleOrder, orderType } = useOrderContext(); // Order data
    const { userData } = useUserContext(); // User Context
    const { cartData } = useCartContext();
    const { items, reloadItems, loading } = useStorageItems();
    const { allProducts, packages, loading: loadingProducts, reloadProducts } = useProducts(); // Products

    const { quickLinks } = useQuickLinks();
    const quickLinkForSub = quickLinks.find(
        (link) => link.subscriber === orderData.recipient // not receiver
    );

    // Billing subscriber lookup
    const [user, setUser] = useState<Subscriber>();
    const [missingPayment, setMissingPayment] = useState<boolean>(false);

    useEffect(() => {
        const u = userData?.subscribers?.find((sub) => sub.subscriber_id === orderData.recipient);
        setUser(u);
    }, [userData]);

    // Default payment method to card
    useEffect(() => {
        if (orderData.paymentMethod === null) {
            handleOrder("paymentMethod", "card");
        }
    }, []);

    useEffect(() => {
        if (!loadingProducts && !allProducts) {
        reloadProducts();
        }
        if (!loading && !items) {
        reloadItems();
        }
    }, [loading, loadingProducts]);

    const cartDatesISO = useMemo(() => {
        const raw = cartData?.selected_dates ?? [];
        return filterDatesOnOrAfterEffectiveTomorrow(raw.map((d) => normalizeDate(d)));
    }, [cartData?.selected_dates]);

    const selectedDatesForUI = cartDatesISO.length > 0
        ? cartDatesISO
        : filterDatesOnOrAfterEffectiveTomorrow(orderData.date);

    // Handle transport - svoz
    const [transportEx, setTransportEx] = useState<boolean>(false);
    useEffect(() => {
        if (!cartData?.items) return;
        const hasTransport = hasTransportForItems(cartData.items, {
            sales: allProducts,
            packages,
            storage: items,
        });
        setTransportEx(hasTransport);
    }, [cartData?.items, allProducts, packages, items]);

    // By default, set the receiver to the logged in user
    useEffect(() => {
        if (!userData.fullname || orderData.receiver !== "") return;
        handleOrder("receiver", userData.fullname);
    }, [userData]);

    useEffect(() => {
        // if no phone in userData or we already have phone in order, do nothing
        if (!userData.phone_number || orderData.phone !== "") return;
        handleOrder("phone", userData.phone_number);
    }, [userData]);

    // Centralized errors
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    // Helpers to set value + clear that field's error
    const setField =
        <OrderType extends keyof typeof orderData>(key: OrderType) =>
        (val: (typeof orderData)[OrderType]) => {
        handleOrder(key, val);
        if (errors[key as string]) {
            setErrors((e) => {
            const { [key as string]: _, ...rest } = e;
            return rest;
            });
        }
        };

    // Validate required data
    const validate = () => {
        const next: Record<string, boolean> = {};
        // Required: delivery time
        if (!orderData.time?.trim() && transportEx) {
        next.time = true;
        }
        // Required: receiver
        if (!orderData.receiver?.trim()) {
        next.receiver = true;
        }
        // Required: Phone
        const phone = String(orderData.phone ?? "").trim();
        if (!phone) {
        next.phone = true;
        }
        // Required: internal code (when subscriber requires it and not paying by card)
        if (!orderData.code?.trim() && user?.code_required && orderData.paymentMethod !== "card") {
        next.code = true;
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    // Continue on valid data
    const continueCheckout = () => {
        let valid = validate();
        if (!orderData.paymentMethod) {
            setMissingPayment(true);
            valid = false;
        }
        if (!valid) return;
        onContinue();
    };

    // Change handlers
    const onEventName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setField("eventName")(e.target.value);
    };
    const onTime = (e: React.ChangeEvent<HTMLInputElement>) => {
        setField("time")(e.target.value);
    };
    const onReceiver = (e: React.ChangeEvent<HTMLInputElement>) => {
        setField("receiver")(e.target.value);
    };
    // Store phone as string (NOT number)
    const onPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        setField("phone")(e.target.value);
    };
    const onFloor = (e: React.ChangeEvent<HTMLInputElement>) => {
        const n = e.currentTarget.valueAsNumber;
        setField("floor")(Number.isNaN(n) ? undefined : n);
    };
    const onRoom = (e: React.ChangeEvent<HTMLInputElement>) => {
        setField("room")(e.target.value);
    };
    const onInternCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        setField("code")(e.target.value);
    };

    return (
        <>
        <div className={styles.CheckoutContent}>
            <div className={styles.CheckoutContentForm}>
            <div className={styles.CheckoutContentGroup}>
                <p className={styles.CheckoutContentTitle}>
                {orderType === "demand" ? t("checkout.steps.2.demand_title") : t("checkout.steps.2.title")}
                </p>
                <div className={styles.CheckoutContentBody}>
                <div className={styles.CheckoutContentFormInputs}>
                    <div className={styles.CheckoutInputGroup}>
                    <input
                        type="text"
                        placeholder={t("checkout.steps.2.inputs.name")}
                        className={`${styles.BasicInputField} ${errors.eventName ? styles.Missing : ""}`}
                        onChange={onEventName}
                        value={orderData.eventName ?? ""}
                        aria-invalid={Boolean(errors.eventName)}
                        aria-describedby={errors.eventName ? "eventName-error" : undefined}
                    />
                    {errors.eventName && (
                        <span id="eventName-error" className={styles.ErrorText}>
                        {t("errors.name")}
                        </span>
                    )}
                    </div>

                    <AddressInput selectedAddress={orderData.address} />

                    <DateInput selectedDates={selectedDatesForUI} />

                    <p className={styles.CheckoutContentSubtitle}>{t("checkout.steps.2.subtitle")}</p>

                    <div className={styles.InputGroup}>
                    <input
                        type="number"
                        placeholder={t("checkout.steps.2.inputs.floor")}
                        min={0}
                        className={`${styles.BasicInputField} ${styles.RoundedInput}`}
                        onChange={onFloor}
                        value={orderData.floor ?? ""}
                    />
                    <input
                        type="text"
                        placeholder={t("checkout.steps.2.inputs.room")}
                        className={`${styles.BasicInputField} ${styles.RoundedInput}`}
                        onChange={onRoom}
                        value={orderData.room ?? ""}
                    />
                    </div>

                    <div className={styles.InputGroup}>
                    <div className={styles.FastPickWrap}>
                        {quickLinkForSub &&
                        quickLinkForSub.floors.map((floor, index) => (
                            <div
                            className={styles.FastPickItem}
                            key={`${floor}-${index}`}
                            onClick={() => setField("floor")(floor)}
                            >
                            <p>{floor}</p>
                            </div>
                        ))}
                    </div>
                    <div className={styles.FastPickWrap}>
                        {quickLinkForSub &&
                        quickLinkForSub.rooms.map((room, index) => (
                            <div
                            className={styles.FastPickItem}
                            key={`${room}-${index}`}
                            onClick={() => setField("room")(room)}
                            >
                            <p>{room}</p>
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
                </div>

                {transportEx && (
                <div className={styles.CheckoutInputGroup}>
                    <p className={styles.CheckoutInputGroupTitle}>{t("checkout.steps.2.preferred_time")}*</p>
                    <input
                    type="text"
                    placeholder={t("checkout.steps.2.inputs.time")}
                    className={`${styles.BasicInputField} ${errors.time ? styles.Missing : ""}`}
                    onChange={onTime}
                    value={orderData.time ?? ""}
                    aria-invalid={Boolean(errors.time)}
                    aria-describedby={errors.time ? "time-error" : undefined}
                    />
                    {errors.time && (
                    <span id="time-error" className={styles.ErrorText}>
                        {t("errors.time")}
                    </span>
                    )}
                </div>
                )}
            </div> 

            <div className={`${styles.CheckoutContentGroup}`}>
                <p className={styles.CheckoutContentTitle}>
                {orderType === "demand" ? t("checkout.steps.2_5.demand_title") : t("checkout.steps.2_5.title")}
                </p>
                <div className={styles.CheckoutContentBody}>
                <div className={styles.CheckoutContentFormInputsGapless}>
                    <div className={styles.Customer}>
                    <p>{userData.fullname ? userData.fullname : "username"}</p>
                    </div>

                    <div className={styles.CheckoutInputGroup}>
                    <p className={styles.CheckoutInputLabel}>{t("checkout.steps.2_5.inputs.recipient")}*</p>
                    <input
                        type="text"
                        placeholder={t("checkout.steps.2_5.inputs.recipient_unset")}
                        className={`${styles.BasicInputField} ${errors.receiver ? styles.Missing : ""}`}
                        onChange={onReceiver}
                        value={orderData.receiver ?? ""}
                        aria-invalid={Boolean(errors.receiver)}
                        aria-describedby={errors.receiver ? "receiver-error" : undefined}
                    />
                    {errors.receiver && (
                        <span id="receiver-error" className={styles.ErrorText}>
                        {t("errors.receiver")}
                        </span>
                    )}

                    <div className={styles.FastPickWrap}>
                        {quickLinkForSub &&
                        quickLinkForSub.names.map((name, index) => (
                            <div
                            className={styles.FastPickItem}
                            key={`${name}-${index}`}
                            onClick={() => setField("receiver")(name)}
                            >
                            <p>{name}</p>
                            </div>
                        ))}
                    </div>
                    </div>

                    <div className={styles.CheckoutInputGroup}>
                    <p className={styles.CheckoutInputLabel}>{t("checkout.steps.2_5.inputs.phone")}*</p>
                    <input
                        type="tel"
                        placeholder={t("checkout.steps.2_5.inputs.phone_unset")}
                        className={`${styles.BasicInputField} ${errors.phone ? styles.Missing : ""}`}
                        onChange={onPhone}
                        value={String(orderData.phone ?? "")}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={errors.phone ? "phone-error" : undefined}
                    />
                    {errors.phone && (
                        <span id="phone-error" className={styles.ErrorText}>
                        {t("errors.phone")}
                        </span>
                    )}

                    <div className={styles.FastPickWrap}>
                        {quickLinkForSub &&
                        quickLinkForSub.numbers.map((number, index) => (
                            <div
                            className={styles.FastPickItem}
                            key={`${number}-${index}`}
                            onClick={() => setField("phone")(number)}
                            >
                            <p>{number}</p>
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
                </div>
            </div>

            <div className={`${styles.CheckoutContentGroup}`}>
                <p className={styles.CheckoutContentTitle}>{t("checkout.steps.3.title")}</p>
                <div className={styles.CheckoutContentBody}>
                <div className={styles.CheckoutContentFormInputs}>
                    <div className={styles.InputGroupColumn}>
                        <p className={styles.InfoGroupTitle}>{t("checkout.steps.3.billing_title")}</p>
                        <div className={styles.Note}>
                            <p>{user?.name}</p>
                            <p>IČ: {user?.ic}</p>
                            {user?.dic && <p>DIČ: {user?.dic}</p>}
                            <p>{userData.email}</p>
                            <p>{cartData?.delivery_adress.adress}</p>
                            <p>{cartData?.delivery_adress.city}</p>
                        </div>
                        <CurrencyInput
                        currency={orderData.paymentMethod}
                        className={missingPayment ? styles.Missing : ""}
                        setCurrency={(value) => {
                            handleOrder("paymentMethod", value);
                            setMissingPayment(false);
                        }}
                        />
                        {missingPayment && (
                            <span id="payment-error" className={styles.ErrorText}>
                                {t("errors.payment")}
                            </span>
                        )}
                    </div>
                    <div className={styles.InputGroupColumn}>
                        <p className={styles.InfoGroupTitle}>{t("checkout.steps.3.extra_title")}</p>
                        <div className={styles.CheckoutInputGroup}>
                            <textarea
                                placeholder={t("checkout.steps.3.inputs.note_placeholder")}
                                className={`${styles.BasicInputField} ${styles.BasicTextArea}`}
                                onChange={(e) => handleOrder("note", e.target.value)}
                                value={orderData.note ?? ""}
                            />
                        </div>

                        <div className={styles.CheckoutInputGroup}>
                            <input
                                type="text"
                                placeholder={t("checkout.steps.3.inputs.code_placeholder")}
                                className={`${styles.BasicInputField} ${errors.code ? styles.Missing : ""}`}
                                onChange={onInternCode}
                                value={orderData.code ?? ""}
                                aria-invalid={Boolean(errors.code)}
                                aria-describedby={errors.code ? "code-error" : undefined}
                            />
                            {errors.code && (
                                <span id="code-error" className={styles.ErrorText}>
                                    {t("errors.code")}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {actionsContainer && createPortal(
                    <div className={styles.ButtonGroup}>
                        <SecondaryButton
                            text={t("buttons.back")}
                            showChevron={false}
                            colorVariant="primary"
                            onClick={() => {
                                onBack && onBack();
                            }}
                            Icon={ChevronLeft}
                            className={styles.BackButton}
                        />
                        <SubmitButton text={t("buttons.proceed")} onClick={continueCheckout} />
                    </div>,
                    actionsContainer
                )}

                <p className={styles.Hint}>{t("checkout.explain")}</p>
                </div>
            </div>
            </div>
        </div>
        </>
    );
};

export default Step2;
